import React, { useState, useEffect } from 'react';
//import { User, LogIn, LogOut, Workflow, MessageSquare, Mic, Ticket, ExternalLink, Activity, HeartPulse, Phone } from 'lucide-react';
import * as Icons from 'lucide-react';
import axios from 'axios';
const { Activity, LogIn, LogOut, ExternalLink } = Icons;

// Auth hosts (HttpOnly cookies cannot be cleared from Hub JS)
const HUB_URL = 'https://hub.sbapps.mybotarmy.com';
const AUTH_URL = 'https://auth.sbapps.mybotarmy.com';
const IDP_URL = 'https://idp.sbapps.mybotarmy.com';
const LOGIN_PAGE_URL = 'https://login.sbapps.mybotarmy.com';

// Login: oauth2-proxy start → back to Hub
const LOGIN_HREF = `${AUTH_URL}/oauth2/start?rd=${encodeURIComponent(HUB_URL)}`;

// Logout: IDP first (connect.sid), then oauth2-proxy (oauth2_auth / _oauth2_proxy*), then login page.
// Order matters — if only oauth2 cookies are cleared, a still-valid connect.sid silently
// re-auths and recreates oauth2_auth, which looks like logout failed.
const OAUTH2_SIGN_OUT = `${AUTH_URL}/oauth2/sign_out?rd=${encodeURIComponent(LOGIN_PAGE_URL)}`;
const LOGOUT_HREF = `${IDP_URL}/end_session?post_logout_redirect_uri=${encodeURIComponent(OAUTH2_SIGN_OUT)}`;

const AppLandingPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState({ email: '', avatar: '' });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping for different app types - updated to handle string names from JSON
  /*
  const iconMap = {
    'Workflow': Workflow,
    'MessageSquare': MessageSquare,
    'Mic': Mic,
    'Ticket': Ticket,
    'HeartPulseIcon': HeartPulse,
    'Phone': Phone,
    'default': Activity
  };
  */


  useEffect(() => {
    checkAuthStatus();
    fetchApps();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get('/api/user');
      setIsLoggedIn(true);
      setUser({
        email: response.data.email,
        avatar: response.data.email.charAt(0).toUpperCase()
      });
    } catch (error) {
      console.error('Auth error:', error);
      setIsLoggedIn(false);
      setUser({ email: '', avatar: '' });
    }
  };

  const fetchApps = async () => {
    try {
      const response = await axios.get('/api/apps');
      console.log('Raw API response:', response.data);
      
      const appsWithIcons = response.data.map(app => {
        const processedApp = {
          ...app,
          // Map the icon string to the actual Lucide component
          icon: Icons[app.icon] || Icons['Activity'],
          // Use the colors directly from JSON - with safety fallback
          colors: app.colors || { gradient: 'from-gray-500 to-slate-500', bg: 'bg-gray-50' }
        };
        console.log(`Processed app ${app.id}:`, processedApp);
        console.log(`App ${app.id} colors:`, processedApp.colors);
        console.log(`App ${app.id} gradient class:`, processedApp.colors.gradient);
        return processedApp;
      });
      
      console.log('Final apps array:', appsWithIcons);
      setApps(appsWithIcons);
    } catch (error) {
      console.error('Error fetching apps:', error);
      // No fallback - rely entirely on API data
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAppClick = (url) => {
    console.log('App clicked! URL:', url);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Title Bar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-white/20 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                SBApps Hub
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {isLoggedIn && (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user.avatar}
                    </div>
                    <span className="text-gray-700 text-sm font-medium hidden sm:block">
                      {user.email}
                    </span>
                  </div>
                  <a
                    href={LOGOUT_HREF}
                    className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <LogOut size={16} />
                    <span className="hidden sm:inline">Logout</span>
                  </a>
                </>
              )}
              {!isLoggedIn && (
                <a
                  href={LOGIN_HREF}
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <LogIn size={16} />
                  <span className="hidden sm:inline">Login</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to Your SBApps Collection
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Access all your essential tools and applications from one dashboard
          </p>
        </div>

        {/* App Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {apps.map((app, index) => {
            const IconComponent = app.icon;
            const isActive = app.status === 'active';
            return (
              <div
                key={app.id || index}
                onClick={isActive ? () => handleAppClick(app.url) : undefined}
                className={`group relative overflow-hidden rounded-2xl transform transition-all duration-300 hocus:scale-105 hocus:-translate-y-2
                  ${isActive ? 'cursor-pointer hover:scale-105 hover:-translate-y-2' : 'opacity-50 grayscale pointer-events-none cursor-not-allowed'}`}
              >
                {/* Glass background */}
                <div className="absolute inset-0 bg-white/20 backdrop-blur-lg border border-white/30 rounded-2xl shadow-xl"></div>
                
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${app.colors.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl`}></div>
                
                {/* Content */}
                <div className="relative p-8 flex flex-col items-center text-center space-y-4 h-48">
                  {/* Icon container */}
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${app.colors.gradient} p-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <IconComponent className="w-full h-full text-white" />
                  </div>
                  
                  {/* App name */}
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition-colors duration-200">
                    {app.name}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200 leading-relaxed break-words line-clamp-2">
                    {app.description}
                  </p>
                  
                  {/* External link indicator (only for active apps) */}
                  {isActive && (
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-60 transition-opacity duration-200">
                      <ExternalLink size={16} className="text-gray-600" />
                    </div>
                  )}
                </div>
                
                {/* Hover effect border */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/40 transition-colors duration-300"></div>
              </div>
            );
          })}
        </div>

        {/* Add more apps placeholder */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30">
            <span className="text-sm">More apps coming soon...</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppLandingPage;
